import { useMutation } from "@tanstack/react-query";
import { gmailSchema } from "@zeeta/shared";
import { ArrowLeft } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { ErrorMessage } from "@/components/error-message";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { useI18n } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import { Turnstile, type TurnstileHandle } from "./turnstile";

interface Challenge {
  challengeId: string;
}

/**
 * "Forgot password": a code to the person's Gmail, then a new password. Signed out, they type
 * their email (and pass the bot check); signed in, the code goes to their own address.
 */
export function PasswordReset({
  signedIn,
  initialEmail = "",
  onDone,
  onBack,
}: {
  signedIn: boolean;
  initialEmail?: string;
  onDone: () => void | Promise<void>;
  onBack?: () => void;
}) {
  const { t, locale } = useI18n();
  const turnstile = useRef<TurnstileHandle>(null);
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string>();
  const [token, setToken] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<(Challenge & { email: string }) | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const send = useMutation({
    mutationFn: async (to: string) =>
      signedIn
        ? api.post<Challenge & { email: string }>("/auth/password/forgot/me", {})
        : {
            ...(await api.post<Challenge>("/auth/password/forgot", {
              email: to,
              turnstileToken: token,
              locale,
            })),
            email: to,
          },
    onSuccess: (result) => {
      setChallenge(result);
      setCode("");
    },
    onSettled: () => turnstile.current?.reset(),
  });

  const reset = useMutation({
    mutationFn: () =>
      api.post("/auth/password/reset", {
        challengeId: challenge?.challengeId,
        code,
        password,
        trustDevice: true,
      }),
    onSuccess: () => onDone(),
  });

  const mismatch = confirmPw.length > 0 && confirmPw !== password;

  function request(e?: FormEvent) {
    e?.preventDefault();
    if (signedIn) return send.mutate("");
    const parsed = gmailSchema.safeParse(email);
    if (!parsed.success) return setEmailError(t("auth.emailInvalid"));
    setEmailError(undefined);
    send.mutate(parsed.data);
  }

  if (!challenge)
    return (
      <form onSubmit={request} className="space-y-4" noValidate>
        {onBack && (
          <Button variant="link" size="sm" onClick={onBack}>
            <ArrowLeft aria-hidden />
            {signedIn ? t("common.cancel") : t("auth.backToSignIn")}
          </Button>
        )}
        {!signedIn && (
          <div className="space-y-1.5">
            <h1 className="text-xl font-semibold tracking-tight">{t("auth.resetTitle")}</h1>
            <p className="text-sm text-muted-foreground">{t("auth.resetSubtitle")}</p>
          </div>
        )}
        {!signedIn && (
          <>
            <Field label={t("auth.emailLabel")} error={emailError}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-base"
                />
              )}
            </Field>
            <Turnstile ref={turnstile} action="password_reset" onToken={setToken} />
          </>
        )}
        <ErrorMessage error={send.error} />
        <Button
          type="submit"
          size={signedIn ? "md" : "lg"}
          variant={signedIn ? "outline" : "primary"}
          className={signedIn ? undefined : "w-full"}
          loading={send.isPending}
          disabled={!signedIn && !token}
        >
          {t("auth.resetSend")}
        </Button>
      </form>
    );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (/^\d{6}$/.test(code) && password.length >= 10 && !mismatch) reset.mutate();
      }}
      className="space-y-4"
      noValidate
    >
      {!signedIn && (
        <Button variant="link" size="sm" onClick={() => setChallenge(null)}>
          <ArrowLeft aria-hidden />
          {t("auth.changeEmail")}
        </Button>
      )}
      <p className="text-sm text-muted-foreground" role="status">
        {t(signedIn ? "auth.resetSentToMe" : "auth.resetSent", { email: challenge.email })}
      </p>
      <Field label={t("auth.codeLabel")}>
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-12 text-center text-2xl tracking-[0.5em] tabular-nums"
          />
        )}
      </Field>
      <Field
        label={t("account.newPassword")}
        hint={t("account.passwordRules")}
        error={reset.error instanceof ApiError ? reset.error.fieldError("password") : undefined}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <Field
        label={t("account.confirmPassword")}
        error={mismatch ? t("account.passwordMismatch") : undefined}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />
        )}
      </Field>
      <ErrorMessage error={reset.error} />
      <Button
        type="submit"
        size={signedIn ? "md" : "lg"}
        className={signedIn ? undefined : "w-full"}
        loading={reset.isPending}
        disabled={code.length !== 6 || password.length < 10 || mismatch || confirmPw.length === 0}
      >
        {t("auth.resetSave")}
      </Button>
    </form>
  );
}
